import React, { useState, useEffect } from 'react';
import {
	Layout,
	Button,
	Progress,
	Card,
	Radio,
	message,
	Tooltip,
} from 'antd';
import { RightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../../components/Dashboard/AppHeader';
import correct from '../../../assets/icons/correct.svg';
import alarm from '../../../assets/icons/alarm.svg';
import useStudentLectureStore from '../../../zustand/store/useStudentLectureStore';
import userStore from '../../../zustand/store/userStore';
import { submitQuizAPI } from '../../../zustand/API';
import InstructionScreen from '../../../components/Dashboard/InstructionScreen';

const { Content } = Layout;
const TIMER_LIMIT = 120;
const indexToLetter = ['a', 'b', 'c', 'd'];

const QuizScreen = () => {
	const [current, setCurrent] = useState(0);
	const [started, setStarted] = useState(false);
	const [answers, setAnswers] = useState([]);
	const [visited, setVisited] = useState(new Set([0]));
	const [locked, setLocked] = useState(new Set());
	const [timer, setTimer] = useState(TIMER_LIMIT);

	const {
		mcqs,
		teacherEmail,
		subject,
		chapterName,
		className,
		resetQuizInfo,
	} = useStudentLectureStore();
	const {user} = userStore();

	const questions = mcqs || [];
	const navigate = useNavigate();

	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

	const cancelQuiz = (reason = 'Quiz cancelled.') => {
		message.error(reason);
		navigate('/dashboard');
	};

	const submitQuiz = async () => {
		const payload = {
			email: user?.email,
			teacherEmail,
			subject,
			className,
			chapterName,
			answers,
		};
		console.log('payload', payload);
		const response = await submitQuizAPI(payload);
		if (response?.status === 200) {
			message.success(response?.data.message);
			resetQuizInfo();
		}
		console.log('Submitted Answers:', answers);
		navigate('/dashboard');
	};

	const handleAnswer = (val) => {
		const updated = [...answers];
		updated[current] = val;
		setAnswers(updated);
	};

	const goToNext = () => {
		if (current < questions.length - 1) {
			setLocked((prev) => new Set(prev).add(current));
			setVisited((prev) => new Set(prev).add(current + 1));
			setCurrent(current + 1);
			setTimer(TIMER_LIMIT);
		}
	};

	useEffect(() => {
		if (!started) return;

		const interval = setInterval(() => {
			setTimer((prev) => {
				if (prev <= 1) {
					clearInterval(interval);
					setLocked((prev) => new Set(prev).add(current));

					if (current < questions.length - 1) {
						setVisited((v) => new Set(v).add(current + 1));
						setCurrent(current + 1);
						return TIMER_LIMIT;
					}
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [current, started]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'F11' && !started) {
				e.preventDefault();
				setStarted(true);
				document.documentElement.requestFullscreen();
			}

			const devKeys = ['F12', 'I', 'J', 'C'];
			if (
				e.key === 'F12' ||
				(e.ctrlKey && e.shiftKey && devKeys.includes(e.key))
			) {
				e.preventDefault();
				cancelQuiz('DevTools detected.');
			}
		};

		const detectDevTools = () => {
			if (window.outerHeight - window.innerHeight > 160) {
				cancelQuiz('DevTools detected via window size.');
			}
		};

		const handleVisibility = () => {
			if (document.hidden) {
				cancelQuiz('Tab switch detected.');
			}
		};

		const handleFullscreen = () => {
			if (started && !document.fullscreenElement) {
				cancelQuiz('Exited fullscreen.');
			}
		};

		const preventDefault = (e) => e.preventDefault();

		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('visibilitychange', handleVisibility);
		document.addEventListener('fullscreenchange', handleFullscreen);
		document.addEventListener('contextmenu', preventDefault);
		document.addEventListener('copy', preventDefault);
		document.addEventListener('cut', preventDefault);
		document.addEventListener('selectstart', preventDefault);

		const interval = setInterval(detectDevTools, 1000);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('visibilitychange', handleVisibility);
			document.removeEventListener('fullscreenchange', handleFullscreen);
			document.removeEventListener('contextmenu', preventDefault);
			document.removeEventListener('copy', preventDefault);
			document.removeEventListener('cut', preventDefault);
			document.removeEventListener('selectstart', preventDefault);
			clearInterval(interval);
		};
	}, [started]);

	if (!started) {
		return (
			<InstructionScreen timerLimit={TIMER_LIMIT}>
				{isMobile ? (
					<div className="text-center mt-6">
						<Button
							type="primary"
							size="large"
							onClick={async () => {
								setStarted(true);
								if (document.documentElement.requestFullscreen) {
									try {
										await document.documentElement.requestFullscreen();
									} catch (err) {
										console.warn('Fullscreen not supported');
									}
								}
							}}
						>
							Start Quiz
						</Button>
					</div>
				) : (
					<p className="text-center text-gray-500 mt-4">
						Press <b>F11</b> to start the quiz.
					</p>
				)}
			</InstructionScreen>
		);
	}

	const currentQuestion = questions[current];
	const progress = Math.round((visited.size / questions.length) * 100);

	return (
		<Content className="p-8 bg-[#f9fafb] min-h-screen">
			<div className="flex justify-between items-center mb-6">
				<h3 className="text-3xl font-bold text-gray-800">
					📝 {subject} : {chapterName}
				</h3>
				<Tooltip title="Time remaining">
					<div
						className={`text-lg font-semibold px-4 py-2 rounded-full border ${
							timer <= 30
								? 'bg-red-100 text-red-600 border-red-200'
								: 'bg-green-100 text-green-600 border-green-200'
						}`}
					>
						<ClockCircleOutlined className="mr-2" />
						{Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
					</div>
				</Tooltip>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<Card className="md:col-span-2 p-6 border rounded-xl shadow-sm bg-white">
					<h4 className="text-xl font-medium mb-4">
						{current + 1}. {currentQuestion?.question}
					</h4>
					<Radio.Group
						className="grid grid-cols-1 sm:grid-cols-2 gap-4"
						value={answers[current]}
						onChange={(e) => handleAnswer(e.target.value)}
					>
						{currentQuestion?.options.map((opt, idx) => (
							<Radio
								key={idx}
								value={indexToLetter[idx]}
								className="w-full p-4 border rounded-lg hover:shadow transition-all"
							>
								<span className="font-medium">{opt}</span>
							</Radio>
						))}
					</Radio.Group>

					<div className="mt-6 flex justify-end">
						{current === questions.length - 1 ? (
							<Button type="primary" size="large" onClick={submitQuiz}>
								Submit Quiz
							</Button>
						) : (
							<Button
								type="primary"
								size="large"
								icon={<RightOutlined />}
								onClick={goToNext}
								disabled={!answers[current]}
							>
								Next Question
							</Button>
						)}
					</div>
				</Card>

				<Card className="p-6 border rounded-xl bg-white shadow-md">
					<h3 className="text-lg font-semibold mb-4 text-gray-700">
						📊 Quiz Stats
					</h3>
					<Progress
						type="circle"
						percent={progress}
						strokeColor="#1890ff"
						className="mx-auto mb-6"
					/>
					<ul className="space-y-4">
						{[
							{
								label: 'Total Questions',
								value: questions.length,
								icon: alarm,
								color: '#ffc107',
							},
							{
								label: 'Visited',
								value: visited.size,
								icon: correct,
								color: '#52c41a',
							},
						].map((item, idx) => (
							<li key={idx} className="flex items-center">
								<div
									className="h-10 w-10 flex items-center justify-center rounded-full"
									style={{ backgroundColor: item.color + '20' }}
								>
									<img src={item.icon} alt={item.label} className="h-5 w-5" />
								</div>
								<div className="ml-3 text-sm">
									<p className="text-gray-600">{item.label}</p>
									<p className="text-base font-semibold">{item.value}</p>
								</div>
							</li>
						))}
					</ul>
				</Card>
			</div>
		</Content>
	);
};

export default QuizScreen;
